import { useIonAlert } from '@ionic/react';
import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { BehaviorSubject, Subscription } from 'rxjs';
import { logTransaction } from '../common/log';
import { LogType } from '../models/log.model';
import storeRef from '../store';
import { setIsInActionState } from '../store/appSlice';
import { signInAsync, signOutAsync } from '../store/authSlice';
import { useAppDispatch } from '../store/hooks';

interface IComponentProps {
  store: typeof storeRef;
  children: ReactNode;
}

/*
  ** This method can be exposed through the public API but can only contain
  ** non-react code
*/
let fireCounter = 0;
const fireSubject: BehaviorSubject<number> = new BehaviorSubject<number>(-1);
const showAuthErrorPub = () => {
  fireCounter++;
  logTransaction(LogType.Warn, `Firing next auth error: ${fireCounter}`);
  fireSubject.next(fireCounter);
};
const clearCounterPub = () => {
  fireCounter = 0;
};
let fireSubscription: Subscription;
let isAlertShown = false;

const AuthErrorProvider: React.FC<IComponentProps> = function ({ store, children }) {
  /*
  ** State can be exposed through the private API
  */
  const dispatch = useAppDispatch();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    if (!fireSubscription) {
      fireSubscription = fireSubject.subscribe((id) => {
        if (id > 0) {
          logTransaction(LogType.Info, `Auth error ${id} detected`);
          showAuthError(id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
  ** This method can be exposed through the private API but can only contain
  ** react specific code
  */
  const showAuthError = async (count: number) => {
    dispatch(setIsInActionState(true));
    const { token } = store.getState().auth;
    //const token = {} as any;

    if (!isAlertShown) {
      logTransaction(LogType.Info, `Prompting user to re-enter password`);
      isAlertShown = true;

      if (count >= 2) {
        await presentAlert({
          header: 'Authentication Error',
          subHeader: '',
          message: 'Please re-enter your password',
          inputs: [
            {
              type: 'text',
              value: token?.displayUsername,
              placeholder: 'username',
            },
            {
              type: 'password',
              value: '',
              placeholder: 'password',
            },
          ],
          buttons: [
            {
              text: 'Retry',
              handler: async (val) => {
                const username = val[0];
                const password = val[1];
                const databaseId = parseInt(token?.databaseId || '-1');
                logTransaction(LogType.Info, `User submitted password`);
                await dispatch(signInAsync({ username, password, databaseId, clearUserData: false }));
              }
            }, {
              text: 'Sign Out',
              handler: () => {
                logTransaction(LogType.Info, `User signed out`);
                dispatch(signOutAsync());
              }
            }
          ],
          onDidDismiss: () => {
            dispatch(setIsInActionState(false));
            isAlertShown = false;
            fireCounter = 0;
          }
        });
      } else {
        logTransaction(LogType.Info, `Prompting user to retry auth`);
        await presentAlert({
          header: 'Authentication Error',
          subHeader: '',
          message: 'Communication between your device and DMS was interrupted',
          buttons: [
            {
              text: 'Retry',
              role: 'cancel'
            }, {
              text: 'Sign Out',
              handler: () => {
                logTransaction(LogType.Info, `User signed out`);
                dispatch(signOutAsync());
              }
            }
          ],
          onDidDismiss: () => {
            dispatch(setIsInActionState(false));
            isAlertShown = false;
          }
        });
      }
    }
  };

  return (
    <AuthErrorContext.Provider value={
      {
        state: {},
        actions: {
          showAuthErrorPub,
          clearCounterPub,
          showAuthError,
        }
      }
    }>
      {children}
    </AuthErrorContext.Provider>
  );
};

export const authErrorProvider = {
  state: {} as {},
  actions: {} as {
    showAuthErrorPub: () => void,
    clearCounterPub: () => void,
    showAuthError: (count: number) => void,
  }
};

/*
** NOTE:
** To use this in react components, use the following code example:
** const { state: {showAuthError}, actions: { privateMethod, setShowAuthError }} = useAuthError();
**
** To use this in non-react components, use the following code example:
** authErrorStatic.publicMethod();
*/

export const AuthErrorContext = createContext<typeof authErrorProvider>(authErrorProvider);
export const useAuthError = () => useContext(AuthErrorContext);

// Expose methods for use in non-react components
export const authErrorStatic = {
  showAuthErrorPub,
  clearCounterPub,
};

export default AuthErrorProvider;
